// The condition builder: adding, nesting and removing the rows of a filter tree.
//
// Takes its element rather than looking one up, and is called by
// listeErgebnis.js, which owns the page. So this module is about interaction
// and knows nothing about when the results refetch.
//
// Two rules that shape the whole file:
//
//   - **Rows come from the server.** The value control's type depends on the
//     chosen field, so picking a field refetches the row from
//     GET /listen/bedingung. Building the control client-side would put a
//     second copy of every input into JavaScript, drifting from the Pug.
//   - **Rows are inserted with insertAdjacentHTML, never innerHTML +=.**
//     Reassigning innerHTML rebuilds every sibling, which throws away a
//     half-typed value in the row next to the one being added - the rule
//     _verlauf already follows.

import { notifyError } from '../../shared/notify.js';

const FRAGMENT_HEADER = 'X-Fragment';

// One below the compiler's cap in utils/listenFelder/feldTypen.js, so the UI
// can never build a tree the server would refuse.
const MAX_TIEFE_UI = 3;

let zaehler = 0;
const naechsteId = () => {
  zaehler += 1;
  return `b${zaehler}`;
};

// Fetches one condition row. `params` carries the entity, the chosen field and
// whatever was already typed, so a refetched row keeps what it can.
async function holeZeile(params) {
  // Built key by key rather than handed to the URLSearchParams constructor,
  // because that constructor stringifies an array into one comma-joined value:
  // `werte: ['a', 'b']` arrives as the single string "a,b", which matches no
  // option, so a reloaded multi-value row rendered with nothing ticked. A
  // repeated key is what Express reads back as an array.
  const query = new window.URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((eintrag) => query.append(key, eintrag));
      return;
    }
    if (value !== '' && value !== null && value !== undefined) query.set(key, value);
  });

  const response = await window.fetch(`/listen/bedingung?${query}`, {
    headers: { [FRAGMENT_HEADER]: '1' },
    credentials: 'same-origin'
  });

  if (!response.ok) throw new Error(`Bedingung konnte nicht geladen werden: ${response.status}`);
  return response.text();
}

// The markup for a nested group. This one *is* built here rather than fetched:
// a group has no field-dependent parts, so there is nothing that could drift.
function gruppenMarkup(tiefe) {
  const amLimit = tiefe >= MAX_TIEFE_UI;

  return [
    `<div class="flex flex-col gap-2 p-2 rounded-lg border border-base-300" data-rolle="gruppe" data-tiefe="${tiefe}">`,
    '<div class="flex items-center gap-2">',
    '<select class="select select-xs" data-rolle="verknuepfung" aria-label="Verknüpfung">',
    '<option value="und">Alle Bedingungen (und)</option>',
    '<option value="oder">Eine der Bedingungen (oder)</option>',
    '</select>',
    '<button type="button" class="btn btn-ghost btn-xs btn-square text-error ml-auto" data-rolle="gruppe-weg" aria-label="Gruppe entfernen">',
    '<i class="fa-solid fa-xmark" aria-hidden="true"></i>',
    '</button>',
    '</div>',
    '<div class="flex flex-col gap-2" data-rolle="regeln"></div>',
    '<div class="flex gap-2">',
    '<button type="button" class="btn btn-xs btn-ghost" data-rolle="bedingung-hinzu">Bedingung</button>',
    // The nesting button hides itself at the cap rather than erroring when
    // pressed - the limit is visible instead of enforced after the fact.
    amLimit
      ? ''
      : '<button type="button" class="btn btn-xs btn-ghost" data-rolle="gruppe-hinzu">Gruppe</button>',
    '</div>',
    '</div>'
  ].join('');
}

/**
 * Wires one filter tree.
 *
 * @param {object} options
 * @param {Element} options.wurzel the tree's root element
 * @param {function} options.onAenderung called whenever the tree changes
 * @returns {{aufbauen: function}|null}
 */
export function initFilterBauer({ wurzel, onAenderung }) {
  if (!wurzel) return null;

  const entitaet = wurzel.dataset.entitaet;
  const melde = () => {
    if (typeof onAenderung === 'function') onAenderung();
  };

  // The container a button belongs to - its own group's rule list, which is
  // what makes "Gruppe" nest inside the group it was pressed in rather than
  // always at the root.
  function regelnVon(el) {
    const gruppe = el.closest('[data-rolle="gruppe"]') || wurzel;
    return gruppe.querySelector(':scope > [data-rolle="regeln"]');
  }

  function tiefeVon(el) {
    const gruppe = el.closest('[data-rolle="gruppe"]');
    return gruppe ? Number(gruppe.dataset.tiefe || 1) : 1;
  }

  async function bedingungHinzu(behaelter) {
    try {
      const markup = await holeZeile({ entitaet, id: naechsteId() });
      behaelter.insertAdjacentHTML('beforeend', markup);
      await optionenNachladen(behaelter.lastElementChild);
      melde();
    } catch (err) {
      notifyError('Die Bedingung konnte nicht geladen werden.');
      throw err;
    }
  }

  // Replaces a row in place after its field changed, carrying across whatever
  // was already entered. The new row's own markup decides which of those values
  // survive - a date typed into a text field is simply not rendered back.
  async function zeileErneuern(zeile) {
    const feld = zeile.querySelector('[data-rolle="feld"]');
    const operator = zeile.querySelector('[data-rolle="operator"]');
    const wert = zeile.querySelector('[data-rolle="wert"]');

    const params = {
      entitaet,
      id: zeile.dataset.id || naechsteId(),
      feld: feld ? feld.value : ''
    };
    if (operator && operator.value) params.operator = operator.value;
    if (wert && wert.value) params.wert = wert.value;

    try {
      const markup = await holeZeile(params);
      zeile.insertAdjacentHTML('afterend', markup);
      const neu = zeile.nextElementSibling;
      zeile.remove();
      await optionenNachladen(neu);
      melde();
    } catch (err) {
      notifyError('Die Bedingung konnte nicht geladen werden.');
      throw err;
    }
  }

  // Fills the ref pickers a row asked for. The endpoint is scoped on the
  // server, so this cannot widen what the picker offers.
  async function optionenNachladen(zeile) {
    if (!zeile) return;

    const einzeln = zeile.querySelector('select[data-optionen]');
    if (einzeln) {
      try {
        const response = await window.fetch(einzeln.dataset.optionen, {
          credentials: 'same-origin'
        });
        const daten = await response.json();
        const optionen = (daten.data && daten.data.optionen) || [];

        einzeln.innerHTML =
          '<option value="">Wert wählen …</option>' +
          optionen.map((o) => `<option value="${o.wert}">${escapeHtml(o.label)}</option>`).join('');
      } catch {
        einzeln.innerHTML = '<option value="">Konnte nicht geladen werden</option>';
      }
    }

    const gruppe = zeile.querySelector('[data-rolle="optionen-gruppe"]');
    if (gruppe) {
      try {
        const response = await window.fetch(gruppe.dataset.optionen, {
          credentials: 'same-origin'
        });
        const daten = await response.json();
        const optionen = (daten.data && daten.data.optionen) || [];
        const gewaehlt = (gruppe.dataset.gewaehlt || '').split(',').filter(Boolean);

        gruppe.innerHTML = optionen
          .map(
            (o) =>
              '<label class="label cursor-pointer justify-start gap-2">' +
              `<input type="checkbox" class="checkbox checkbox-sm checkbox-primary" data-rolle="wert" value="${o.wert}"${gewaehlt.includes(o.wert) ? ' checked' : ''}>` +
              `<span class="text-sm">${escapeHtml(o.label)}</span>` +
              '</label>'
          )
          .join('');
      } catch {
        gruppe.innerHTML = '<span class="text-sm">Konnte nicht geladen werden</span>';
      }
    }
  }

  // One delegated listener for the whole tree, so rows and groups arriving at
  // any depth are live without rebinding.
  wurzel.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button || !wurzel.contains(button)) return;

    const rolle = button.dataset.rolle;

    if (rolle === 'bedingung-hinzu') {
      event.preventDefault();
      bedingungHinzu(regelnVon(button));
      return;
    }

    if (rolle === 'gruppe-hinzu') {
      event.preventDefault();
      const behaelter = regelnVon(button);
      behaelter.insertAdjacentHTML('beforeend', gruppenMarkup(tiefeVon(button) + 1));
      melde();
      return;
    }

    if (rolle === 'entfernen') {
      event.preventDefault();
      const zeile = button.closest('[data-rolle="bedingung"]');
      if (zeile) zeile.remove();
      melde();
      return;
    }

    if (rolle === 'gruppe-weg') {
      event.preventDefault();
      const gruppe = button.closest('[data-rolle="gruppe"]');
      if (gruppe) gruppe.remove();
      melde();
    }
  });

  wurzel.addEventListener('change', (event) => {
    const el = event.target;

    // A changed field means a different value control, so the row is refetched.
    if (el.matches('[data-rolle="feld"]')) {
      const zeile = el.closest('[data-rolle="bedingung"]');
      if (zeile) zeileErneuern(zeile);
      return;
    }

    // An operator change can also change the value shape - "ist" to "zwischen"
    // needs two inputs where there was one.
    if (el.matches('[data-rolle="operator"]')) {
      const zeile = el.closest('[data-rolle="bedingung"]');
      if (zeile) zeileErneuern(zeile);
      return;
    }

    melde();
  });

  // Typing in a value acts on input rather than change, so the row count keeps
  // up while somebody types. listeErgebnis.js debounces the refetch.
  wurzel.addEventListener('input', (event) => {
    if (event.target.matches('[data-rolle="wert"], [data-rolle="von"], [data-rolle="bis"]')) {
      melde();
    }
  });

  /**
   * Rebuilds the tree from a saved definition.
   *
   * What the "Zurücksetzen" button calls, and what runs on first load: the
   * server rendered the definition into data-definition, so a reset needs no
   * round trip of its own for the shape - only for each row's markup.
   */
  async function aufbauen(baum) {
    const behaelter = wurzel.querySelector(':scope > [data-rolle="regeln"]');
    if (!behaelter) return;

    behaelter.innerHTML = '';

    const verknuepfung = wurzel.querySelector('[data-rolle="verknuepfung"]');
    if (verknuepfung) verknuepfung.value = baum && baum.verknuepfung === 'oder' ? 'oder' : 'und';

    if (!baum || !Array.isArray(baum.regeln)) return;

    await baumEinsetzen(baum, behaelter, 1);
  }

  async function baumEinsetzen(gruppe, behaelter, tiefe) {
    for (const regel of gruppe.regeln) {
      if (regel && Array.isArray(regel.regeln)) {
        if (tiefe >= MAX_TIEFE_UI) continue;

        behaelter.insertAdjacentHTML('beforeend', gruppenMarkup(tiefe + 1));
        const neueGruppe = behaelter.lastElementChild;

        const wahl = neueGruppe.querySelector('[data-rolle="verknuepfung"]');
        if (wahl) wahl.value = regel.verknuepfung === 'oder' ? 'oder' : 'und';

        await baumEinsetzen(
          regel,
          neueGruppe.querySelector(':scope > [data-rolle="regeln"]'),
          tiefe + 1
        );
        continue;
      }

      // Sequential rather than parallel: each row is appended to the same
      // container, and Promise.all would let them land out of order.
      const markup = await holeZeile({
        entitaet,
        id: naechsteId(),
        feld: regel.feld || '',
        operator: regel.operator || '',
        wert: regel.wert === undefined ? '' : regel.wert,
        von: regel.von || '',
        bis: regel.bis || '',
        fenster: regel.fenster || '',
        werte: regel.werte || []
      });

      behaelter.insertAdjacentHTML('beforeend', markup);
      await optionenNachladen(behaelter.lastElementChild);
    }
  }

  return { aufbauen };
}

// The option labels come from the database - a customer name can contain
// anything - and they are being built into markup here rather than set as text,
// so they are escaped.
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
